// src/components/Recruiter/PostJob.jsx
import React, { useState } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { EXPERIENCE_LEVEL_OPTIONS } from '../../utils/experienceLevels';
import { provincesWithCities, getCitiesByProvince } from '../../utils/cities';
import { getRecruiterTokens, deductTokens } from '../../services/recruiter';

const PostJob = ({ onSuccess, recruiterId }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        requirements: '',
        salary: '',
        closingDate: '',
        province: '',
        city: '',
        workType: '',
        experienceLevel: '',
        company: '',
        isSponsored: false,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [availableCities, setAvailableCities] = useState([]);
    const [tokenBalance, setTokenBalance] = useState(0);
    const [checkingTokens, setCheckingTokens] = useState(false);

    const workTypes = ['Remote', 'Hybrid', 'On-site'];

    // Check token balance when component loads
    React.useEffect(() => {
        const checkTokens = async () => {
            if (recruiterId) {
                const tokens = await getRecruiterTokens(recruiterId);
                setTokenBalance(tokens);
            }
        };
        checkTokens();
    }, [recruiterId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleProvinceChange = (e) => {
        const province = e.target.value;
        const cities = getCitiesByProvince(province);
        setAvailableCities(cities);
        setFormData(prev => ({ ...prev, province, city: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validate required fields
        if (!formData.title || !formData.description || !formData.closingDate || 
            !formData.province || !formData.workType || !formData.experienceLevel) {
            setError('Please fill in all required fields');
            setLoading(false);
            return;
        }

        // Check if sponsoring and has enough tokens
        if (formData.isSponsored && tokenBalance < 1) {
            setError('Insufficient tokens to sponsor this job. You need 1 token.');
            setLoading(false);
            return;
        }

        try {
            // If sponsored, deduct token
            if (formData.isSponsored) {
                const deductResult = await deductTokens(
                    recruiterId,
                    1,
                    `Sponsored job: ${formData.title}`
                );
                if (!deductResult.success) {
                    setError('Failed to deduct token: ' + deductResult.error);
                    setLoading(false);
                    return;
                }
            }

            const jobData = {
                ...formData,
                postedBy: recruiterId,
                postedByType: 'recruiter',
                jobType: 'internal',
                applications: [],
                isSponsored: formData.isSponsored || false,
                sponsoredUntil: formData.isSponsored ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() : null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                applicationCount: 0,
                company: formData.company || 'Company Name'
            };

            await addDoc(collection(db, 'jobs'), jobData);
            
            // Refresh token balance
            const newTokens = await getRecruiterTokens(recruiterId);
            setTokenBalance(newTokens);
            
            onSuccess();
            setFormData({
                title: '',
                description: '',
                requirements: '',
                salary: '',
                closingDate: '',
                province: '',
                city: '',
                workType: '',
                experienceLevel: '',
                company: '',
                isSponsored: false,
            });
            alert('Job posted successfully!' + (formData.isSponsored ? ' 🎉 Sponsored for 7 days!' : ''));
        } catch (error) {
            console.error("Error posting job:", error);
            setError('Failed to post job. Please try again.');
        }
        setLoading(false);
    };

    return (
        <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold text-gradpath-dark mb-4">Post a New Job</h3>
            
            {/* Token Balance Display */}
            <div className="bg-gray-50 p-3 rounded-lg mb-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">Your Token Balance:</span>
                <span className="font-bold text-gradpath-green">{tokenBalance} tokens</span>
            </div>
            
            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Job Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Company Name
                        </label>
                        <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your company name"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Job Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="5"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Requirements (Optional)
                    </label>
                    <textarea
                        name="requirements"
                        value={formData.requirements}
                        onChange={handleChange}
                        rows="3"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Salary (Optional)
                        </label>
                        <input
                            type="text"
                            name="salary"
                            value={formData.salary}
                            onChange={handleChange}
                            placeholder="e.g. R250,000 - R350,000"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Closing Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            name="closingDate"
                            value={formData.closingDate}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            required
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Province <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="province"
                            value={formData.province}
                            onChange={handleProvinceChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            required
                        >
                            <option value="">Select Province</option>
                            {Object.keys(provincesWithCities).map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            City <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            required
                        >
                            <option value="">Select City</option>
                            {availableCities.map(city => (
                                <option key={city} value={city}>{city}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Work Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="workType"
                            value={formData.workType}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                            required
                        >
                            <option value="">Select Work Type</option>
                            {workTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Experience Level <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="experienceLevel"
                        value={formData.experienceLevel}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                        required
                    >
                        <option value="">Select Experience Level</option>
                        {EXPERIENCE_LEVEL_OPTIONS.map(level => (
                            <option key={level.value} value={level.value}>{level.label}</option>
                        ))}
                    </select>
                </div>

                {/* SPONSOR OPTION */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            name="isSponsored"
                            checked={formData.isSponsored}
                            onChange={handleChange}
                            className="mt-1 w-4 h-4 text-gradpath-green rounded focus:ring-gradpath-green"
                        />
                        <div>
                            <label className="font-medium text-gray-700 cursor-pointer">
                                Sponsor this job <span className="text-xs text-yellow-600 font-bold">(1 token)</span>
                            </label>
                            <p className="text-xs text-gray-500">
                                Your job will be featured at the top of the job board for 7 days with a "Sponsored" badge.
                                {tokenBalance < 1 && (
                                    <span className="block text-red-500 mt-1">
                                        ⚠️ Insufficient tokens. Please contact admin to add tokens.
                                    </span>
                                )}
                            </p>
                            {formData.isSponsored && tokenBalance >= 1 && (
                                <p className="text-xs text-green-600 mt-1">
                                    ✅ 1 token will be deducted. Remaining: {tokenBalance - 1} tokens
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={loading || (formData.isSponsored && tokenBalance < 1)}
                    className={`w-full font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2 ${
                        loading || (formData.isSponsored && tokenBalance < 1)
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradpath-green text-white hover:bg-gradpath-dark'
                    }`}
                >
                    {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
                    {loading ? 'Posting...' : formData.isSponsored ? 'Post Sponsored Job' : 'Post Job'}
                </button>
            </form>
        </div>
    );
};

export default PostJob;